import { useState, useCallback, useMemo, useEffect } from "react";

import { useHistory, useLocation } from "react-router-dom";

import { DEFAULT_PAGE_SIZE, URL_PAGE_PARAM } from "../constants";

interface UseTablePaginationOptions {
  totalCount?: number;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  enableURLPagination?: boolean;
}

export const useTablePagination = ({
  totalCount = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  currentPage: controlledPage,
  onPageChange: controlledOnPageChange,
  enableURLPagination = true,
}: UseTablePaginationOptions) => {
  const isControlled = controlledPage !== undefined;
  const history = useHistory();
  const location = useLocation();

  // Derive page from router location reactively (reacts to browser back/forward)
  const pageFromURL = useMemo(() => {
    if (!enableURLPagination) return 1;
    const params = new URLSearchParams(location.search);
    const page = params.get(URL_PAGE_PARAM);

    return page ? Math.max(1, parseInt(page, 10) || 1) : 1;
  }, [location.search, enableURLPagination]);

  // Used only when enableURLPagination=false and uncontrolled
  const [internalPage, setInternalPage] = useState<number>(1);

  const currentPage = isControlled
    ? controlledPage
    : enableURLPagination
      ? pageFromURL
      : internalPage;

  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

  const onPageChange = useCallback(
    (page: number) => {
      const clampedPage = Math.min(Math.max(1, page), pageCount);

      if (!isControlled && !enableURLPagination) {
        setInternalPage(clampedPage);
      }

      controlledOnPageChange?.(clampedPage);

      if (enableURLPagination) {
        const searchParams = new URLSearchParams(history.location.search);
        if (clampedPage <= 1) {
          searchParams.delete(URL_PAGE_PARAM);
        } else {
          searchParams.set(URL_PAGE_PARAM, String(clampedPage));
        }
        history.push({ search: searchParams.toString() });
      }
    },
    [
      pageCount,
      isControlled,
      controlledOnPageChange,
      enableURLPagination,
      history,
    ]
  );

  useEffect(() => {
    if (currentPage > pageCount && pageCount > 0) {
      onPageChange(pageCount);
    }
  }, [currentPage, pageCount, onPageChange]);

  const paginationState = useMemo(
    () => ({
      pageIndex: currentPage - 1,
      pageSize,
    }),
    [currentPage, pageSize]
  );

  return {
    currentPage,
    pageCount,
    pageSize,
    paginationState,
    onPageChange,
  };
};
